/**
 * One player of a game: a seat (the devices the player holds and the headband they picked), a
 * simulation of the course, the input that feeds it, the interpolated view, the viewport that
 * draws the world around it with the player's own silhouette and the other players as ghosts, and
 * the run clock over it. A single-player game is one of these over the whole screen; a
 * split-screen race stacks one per seat, all ticked in lockstep by the same loop.
 *
 * The ghost of another player is that player's live view drawn through a second silhouette
 * renderer in this viewport, so it stands exactly where the other player is. Online play later
 * feeds the same renderer a view built from a peer's state instead of a local simulation.
 */

import {
  conditioningFromTuning,
  GamepadReader,
  type InputConditioning,
  InputSource,
  type KeyboardReader,
  TouchReader,
} from "@lache/input";
import {
  Backdrop,
  BoostEffect,
  DebugRenderer,
  headband,
  type Pose,
  type PoseName,
  type RenderFeel,
  type ShapeFactory,
  SilhouetteRenderer,
  Viewport,
  ViewState,
  WorldRenderer,
} from "@lache/render";
import { type CourseData, createInputFrame, HZ, type InputFrame, Sim, type Tuning } from "@lache/sim";
import { Container } from "pixi.js";
import type { Appearance } from "./appearance";
import { CharacterAppearanceRenderer } from "./appearance-renderer";
import { rebasePracticeSim } from "./infinite-state";
import { MenuGate } from "./menu";
import { Recorder } from "./recorder";
import { ClockMode, RunHud } from "./screens";

/** The devices a player holds and the look they chose. */
export interface Seat {
  /** Gamepad API index of the seat's pad, or -1 for the `slot`-th connected pad. */
  pad: number;
  /** With `pad` -1: which connected pad, in the browser's order (0 is the first), or -1 for no pad. */
  slot: number;
  /** Whether the keyboard drives this seat. */
  keyboard: boolean;
  /** Headband color, an index into HEADBANDS. */
  band: number;
  /** Pad scheme index (`docs/controls.md`). */
  scheme: number;
}

/** A single player's seat: the keyboard, the first pad, and (through the app) the touch controls. */
export function soloSeat(band: number, scheme: number): Seat {
  return { pad: -1, slot: 0, keyboard: true, band, scheme };
}

/** What a ghost is drawn from: a live view and the simulation it belongs to (another player, a replayed run, later a peer). */
export interface GhostSource {
  readonly view: ViewState;
  readonly sim: Sim;
  readonly appearance?: Appearance | null;
}

interface Ghost {
  root: Container;
  body: SilhouetteRenderer;
  source: GhostSource;
  /** Another player of this game (shown by the ghost setting), or a run (shown while it is armed). */
  kind: "player" | "run";
  active: boolean;
  appearance: CharacterAppearanceRenderer | null;
  nativeAccent: number;
}

export class Player {
  seat: Seat;
  sim: Sim;
  readonly input: InputSource;
  readonly liveInput: InputFrame = createInputFrame();
  readonly tickInput: InputFrame = createInputFrame();
  /** A frame from the dev API that replaces the devices while set. */
  overrideInput: InputFrame | null = null;
  /** How this seat's devices are read: the tuning's thresholds with the seat's own pad scheme. */
  cond: InputConditioning;
  /** The pause button's state at the last live sample, for its press edge. */
  prevPause = true;
  readonly gate = new MenuGate();
  readonly view = new ViewState();
  readonly viewport = new Viewport();
  /** The night sky behind this player's world, and the world itself in neon. */
  readonly backdrop: Backdrop;
  world: WorldRenderer;
  debug: DebugRenderer;
  readonly silhouette: SilhouetteRenderer;
  private currentAppearance: Appearance | null = null;
  private appearanceRenderer: CharacterAppearanceRenderer | null = null;
  /** The streaks of the speed burst, behind the body (`boost.ts`). */
  readonly boost: BoostEffect;
  /** The ghosts drawn in this viewport: the other players ("p1"), the best-time run ("best"). */
  readonly ghosts = new Map<string, Ghost>();
  /** Records every tick since the last restart, and plays a tape back in place of the devices. */
  readonly recorder = new Recorder();
  /** The course's name, for the tapes. */
  courseName: string;
  /** Accumulated origin shifts of the streamed practice world, for continuous backdrop parallax. */
  worldOriginX = 0;
  /** DOM over this player's viewport: the run clock and the seat tag. */
  readonly hudRoot: HTMLDivElement;
  readonly hud: RunHud;
  private readonly tag: HTMLDivElement;
  /** Split times of the current run by respawn index, from Checkpoint events. */
  runSplits: number[] = [];
  /** Splits of the best run on the board for this player, for the split flashes. */
  bestSplits: number[] = [];
  /** Ticks at which the gym's marks were passed, for the telemetry split. */
  readonly markTicks = new Map<number, number>();
  splitText = "";
  /** The finished run's time and falls, or null until the finish line. */
  finish: { time: number; falls: number } | null = null;
  /** The tick the current run's clock started (the RunStarted event), for the saved run. */
  runStartTick = 0;
  /** Whether the HUD already says this player is waiting for the others to finish (a race). */
  waitingShown = false;

  constructor(
    readonly index: number,
    seat: Seat,
    course: CourseData,
    tuning: Tuning,
    feel: RenderFeel,
    poses: Record<PoseName, Pose>,
    private readonly shapes: ShapeFactory,
    private readonly poseSet: Record<PoseName, Pose>,
    keyboard: KeyboardReader,
    touch: TouchReader | null,
    stage: Container,
    hudParent: HTMLElement,
  ) {
    this.seat = seat;
    this.sim = new Sim(course, tuning);
    this.courseName = course.name;
    this.cond = conditioningFromTuning(tuning);
    this.input = new InputSource(
      keyboard,
      new GamepadReader(undefined, seat.pad, seat.slot),
      touch ?? new TouchReader(),
    );
    this.backdrop = new Backdrop(shapes);
    this.viewport.backdrop.addChild(this.backdrop.root);
    this.world = new WorldRenderer(shapes, this.sim.world, feel);
    this.viewport.world.addChild(this.world.root);
    this.debug = new DebugRenderer(shapes, this.sim.world, this.sim.tuning);
    this.viewport.world.addChild(this.debug.root);
    // The speed burst sits behind the body: the streaks are what it pulls away from.
    this.boost = new BoostEffect(shapes, this.viewport.world);
    this.silhouette = new SilhouetteRenderer(shapes, poses);
    this.viewport.world.addChild(this.silhouette.root);
    stage.addChild(this.viewport.root);
    this.hudRoot = document.createElement("div");
    this.hudRoot.className = "player-hud";
    hudParent.appendChild(this.hudRoot);
    this.hud = new RunHud(this.hudRoot);
    this.tag = document.createElement("div");
    this.tag.className = "seat-tag";
    this.tag.hidden = true;
    this.hudRoot.appendChild(this.tag);
    this.applySeat(seat);
  }

  /** Bind the devices and the look of a seat (the join screen's choices, or the settings menu's). */
  applySeat(seat: Seat): void {
    this.seat = seat;
    this.input.gamepad.padIndex = seat.pad;
    this.input.gamepad.slot = seat.slot;
    this.input.keyboardEnabled = seat.keyboard;
    this.cond.padScheme = seat.scheme;
    const accent = headband(seat.band).color;
    if (this.appearanceRenderer) this.appearanceRenderer.setNativeAccent(accent);
    else this.silhouette.setAccent(accent);
    this.tag.style.setProperty("--band", `#${headband(seat.band).color.toString(16).padStart(6, "0")}`);
  }

  get appearance(): Appearance | null {
    return this.currentAppearance;
  }

  /** Cosmetics follow the display pose, without changing the player's simulation. */
  setAppearance(appearance: Appearance | null): void {
    this.currentAppearance = appearance ? { ...appearance } : null;
    if (appearance && !this.appearanceRenderer) {
      this.appearanceRenderer = new CharacterAppearanceRenderer(
        this.shapes,
        this.viewport.world,
        this.silhouette,
        headband(this.seat.band).color,
      );
    }
    this.appearanceRenderer?.setAppearance(this.currentAppearance);
  }

  get appearanceInfo() {
    return { appearance: this.currentAppearance, render: this.appearanceRenderer?.metadata ?? null };
  }

  /** Show "P1" in the seat's color over the viewport (a race), or nothing (a single player). */
  setTag(text: string | null): void {
    this.tag.hidden = text === null;
    if (text !== null) this.tag.textContent = text;
  }

  /** A tuning change: the simulation recompiles it and the devices take its thresholds. */
  setTuning(tuning: Tuning): void {
    this.sim.setTuning(tuning);
    this.cond = conditioningFromTuning(tuning);
    this.cond.padScheme = this.seat.scheme;
  }

  /** Switch the simulation to a course (rebuilding its drawing) and keep the tuning. */
  setCourse(course: CourseData, tuning: Tuning, feel: RenderFeel): void {
    this.sim = new Sim(course, tuning);
    this.courseName = course.name;
    this.worldOriginX = 0;
    this.appearanceRenderer?.reset();
    this.rebuildWorld(feel);
    this.debug.destroy();
    this.debug = new DebugRenderer(this.shapes, this.sim.world, this.sim.tuning);
    this.viewport.world.addChildAt(this.debug.root, 1);
  }

  /** Replace practice terrain around the runner while keeping their motion and camera continuous. */
  streamCourse(course: CourseData, offset: number, feel: RenderFeel): void {
    this.sim = rebasePracticeSim(this.sim, course, offset);
    this.worldOriginX += offset;
    this.viewport.camera.x -= offset;
    this.view.x -= offset;
    this.view.shoulderX -= offset;
    this.view.handX -= offset;
    this.view.barId = this.sim.state.barId;
    this.view.lastBarId = this.sim.state.lastBarId;
    this.view.assistBar = this.sim.state.assistBar;
    this.view.ledgeSolid = this.sim.state.ledgeSolid;
    this.silhouette.shiftOrigin(offset);
    this.appearanceRenderer?.shiftOrigin(offset);
    this.rebuildWorld(feel);
    const debugVisible = this.debug.root.visible;
    this.debug.destroy();
    this.debug = new DebugRenderer(this.shapes, this.sim.world, this.sim.tuning);
    this.debug.root.visible = debugVisible;
    this.viewport.world.addChildAt(this.debug.root, 1);
  }

  /** Redraw the world's statics (a course change, or a feel value of the look moved). */
  rebuildWorld(feel: RenderFeel): void {
    this.world.destroy();
    this.world = new WorldRenderer(this.shapes, this.sim.world, feel);
    this.viewport.world.addChildAt(this.world.root, 0);
  }

  /** Draw `other` as a ghost in this viewport (behind this player's own silhouette). */
  addGhost(other: Player): void {
    this.addGhostSource(`p${other.index}`, other, headband(other.seat.band).color, "player");
  }

  /** A ghost's headband follows the other player's seat. */
  syncGhost(other: Player): void {
    this.setGhostAccent(`p${other.index}`, headband(other.seat.band).color);
    const ghost = this.ghosts.get(`p${other.index}`);
    if (ghost) this.syncGhostAppearance(ghost);
  }

  /** Draw any view as a ghost here, under `key` (a run's ghost is shown while `setGhostActive` says so). */
  addGhostSource(key: string, source: GhostSource, color: number, kind: "player" | "run"): void {
    const existing = this.ghosts.get(key);
    if (existing) {
      existing.source = source;
      this.setGhostAccent(key, color);
      this.syncGhostAppearance(existing);
      return;
    }
    const root = new Container();
    const body = new SilhouetteRenderer(this.shapes, this.poseSet);
    body.drawHeldLadderBar = true;
    root.addChild(body.root);
    // Ghosts sit between the world's statics and this player's body.
    const localBack = this.appearanceRenderer?.backLayer ?? this.silhouette.root;
    this.viewport.world.addChildAt(root, this.viewport.world.getChildIndex(localBack));
    body.setAccent(color);
    const ghost: Ghost = {
      root,
      body,
      source,
      kind,
      active: kind === "player",
      appearance: null,
      nativeAccent: color,
    };
    this.ghosts.set(key, ghost);
    this.syncGhostAppearance(ghost);
  }

  private syncGhostAppearance(ghost: Ghost): void {
    const appearance = ghost.source.appearance ?? null;
    if (appearance && !ghost.appearance) {
      ghost.appearance = new CharacterAppearanceRenderer(
        this.shapes,
        ghost.root,
        ghost.body,
        ghost.nativeAccent,
      );
    }
    ghost.appearance?.setAppearance(appearance);
  }

  setGhostAccent(key: string, color: number): void {
    const ghost = this.ghosts.get(key);
    if (!ghost) return;
    ghost.nativeAccent = color;
    if (ghost.appearance) ghost.appearance.setNativeAccent(color);
    else ghost.body.setAccent(color);
  }

  /** Show or hide a run's ghost (a player's ghost follows the ghost setting instead). */
  setGhostActive(key: string, active: boolean): void {
    const g = this.ghosts.get(key);
    if (g) g.active = active;
  }

  removeGhost(key: string): void {
    const g = this.ghosts.get(key);
    if (!g) return;
    g.appearance?.destroy();
    this.viewport.world.removeChild(g.root);
    g.root.destroy({ children: true, texture: false, textureSource: false });
    this.ghosts.delete(key);
  }

  /** Back to a respawn point (0 is the start) with the run bookkeeping cleared and a fresh recording. */
  restart(respawn: number): void {
    this.sim.reset(respawn);
    this.appearanceRenderer?.reset();
    this.recorder.start(this.courseName, this.sim.tuning.hash, respawn);
    this.viewport.popups.clear();
    this.boost.clear();
    this.markTicks.clear();
    this.splitText = "";
    this.runSplits = [];
    this.finish = null;
    this.runStartTick = 0;
    this.waitingShown = false;
    this.hud.clearSplit();
    if (this.sim.world.timed) this.hud.setMode(ClockMode.Ready);
    else this.hud.setMode(ClockMode.Hidden);
  }

  /** Blend the two newest ticks into the view; every player's view is updated before any ghost draws. */
  updateView(alpha: number, feel: RenderFeel, dt: number): void {
    this.view.update(this.sim.prev, this.sim.state, alpha, this.sim.tuning, feel, dt);
  }

  /** Follow the view with the camera and draw the world, the body, the ghosts, and the popups. */
  draw(
    feel: RenderFeel,
    dt: number,
    timestamp: number,
    showBody: boolean,
    showGhosts: boolean,
    players: readonly Player[],
  ): void {
    const vp = this.viewport;
    vp.camera.update(this.view, this.sim.tuning.maxRunSpeed, feel, dt);
    vp.applyCamera();
    this.backdrop.update(vp.camera, feel, timestamp, this.worldOriginX);
    this.world.update(feel, timestamp, this.view.barId, this.view.handY);
    this.debug.draw(this.view, this.sim, vp.camera, feel, timestamp);
    this.silhouette.root.visible = showBody;
    if (showBody) {
      this.silhouette.draw(this.view, this.sim, this.appearanceRenderer?.drawFeel(feel) ?? feel, dt);
    }
    this.appearanceRenderer?.draw(feel, dt, timestamp / 1000, undefined, this.view.vx);
    for (const ghost of this.ghosts.values()) {
      // Another player's ghost follows the ghost setting and must still be in the game; a run's
      // ghost shows while it is armed. Neither draws without the bodies (the debug renderer).
      const visible =
        showBody &&
        (ghost.kind === "player" ? showGhosts && players.includes(ghost.source as Player) : ghost.active);
      ghost.root.visible = visible;
      if (visible) {
        ghost.root.alpha = feel.ghostAlpha;
        ghost.body.draw(ghost.source.view, ghost.source.sim, ghost.appearance?.drawFeel(feel) ?? feel, dt);
        ghost.appearance?.draw(feel, dt, timestamp / 1000, undefined, ghost.source.view.vx);
      }
    }
    this.boost.draw(this.view, feel, (this.view.time * 1000) / HZ);
    vp.popups.draw(this.view, vp.camera, feel, (this.view.time * 1000) / HZ);
  }

  destroy(): void {
    this.appearanceRenderer?.destroy();
    for (const ghost of this.ghosts.values()) ghost.appearance?.destroy();
    this.world.destroy();
    this.debug.destroy();
    this.viewport.destroy();
    this.hudRoot.remove();
  }
}
